<template>
  <div>
    <b-card>
      <b-row class="mb-1">
        <b-col lg="4" />
        <b-col
          lg="8"
          class="d-flex justify-content-end align-items-center"
        >
          <template v-if="$permissionAbility(USER_CREATE, permissions)">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              class="flex-shrink-0"
              variant="primary"
              @click="showUserModal"
            >
              Create User
            </b-button>
          </template>
        </b-col>
      </b-row>

      <!-- table -->
      <vue-good-table
        mode="remote"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :rows="rows"
        :sort-options="{
          enabled: false,
          multipleColumns: true,
          initialSortBy: [
            { field: 'id', type: 'desc' },
            { field: 'name', type: 'desc' },
            { field: 'created_at', type: 'desc' },
          ],
        }"
        :columns="columns"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
        style-class="vgt-table striped"
        @on-page-change="onPageChange"
        @on-sort-change="onSortChange"
        @on-column-filter="onColumnFilter"
        @on-per-page-change="onPerPageChange"
      >
        <template
          slot="table-row"
          slot-scope="props"
        >
          <span v-if="props.column.field === 'sl'">
            {{ (serverParams.page - 1) * serverParams.perPage + props.index + 1 }}
          </span>

          <span v-if="props.column.field === 'status_formated'">
            <span
              v-if="props.row.status === 1"
              class="badge bg-success"
            >Active</span>
            <span
              v-else
              class="badge bg-danger"
            >Inactive</span>
          </span>

          <span v-if="props.column.field === 'created_at_formated'">
            {{
              new Date(props.row.created_at).toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })
            }}
          </span>

          <span v-if="props.column.field === 'action'">
            <template v-if="$permissionAbility(USER_EDIT, permissions)">
              <span @click="onShow(props.row)">
                <feather-icon
                  v-b-tooltip.hover
                  icon="Edit2Icon"
                  class="mr-50 custom-icon cursor-pointer"
                  title="Edit"
                  size="16"
                />
              </span>
            </template>
            <template v-if="$permissionAbility(USER_DELETE, permissions)">
              <span>
                <feather-icon
                  v-b-tooltip.hover
                  icon="Trash2Icon"
                  class="mr-50 custom-icon cursor-pointer"
                  title="Delete"
                  size="16"
                  @click="onDelete(props.row)"
                />
              </span>
            </template>
            <!-- View Roles Button -->
            <span @click="viewRoles(props.row)">
              <feather-icon
                v-b-tooltip.hover
                icon="EyeIcon"
                class="mr-50 custom-icon cursor-pointer"
                title="View Roles"
                size="16"
              />
            </span>
          </span>

          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <template
          slot="pagination-bottom"
          slot-scope="props"
        >
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap"> Showing 1 to </span>
              <b-form-select
                v-model="pageLength"
                :options="['10', '25', '50', '100', '500']"
                class="mx-1"
                @input="(value) => props.perPageChanged({ currentPerPage: value })"
              />
              <span class="text-nowrap"> of {{ props.total }} entries </span>
            </div>
            <div>
              <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </b-card>

    <b-modal
      id="modal-view-roles"
      centered
      :title="'Roles Assigned to ' + selectedUser.name"
      hide-footer
      size="md"
      @hidden="hiddenRolesModal"
    >
      <b-list-group>
        <b-list-group-item
          v-for="role in selectedUser.roles"
          :key="role.id"
        >
          {{ role.name }}
        </b-list-group-item>
        <b-list-group-item
          v-if="!selectedUser.roles || selectedUser.roles.length === 0"
          class="text-muted"
        >
          No roles assigned
        </b-list-group-item>
      </b-list-group>
      <div class="mt-3 text-right">
        <b-button
          variant="secondary"
          @click="$bvModal.hide('modal-view-roles')"
        >
          Close
        </b-button>
      </div>
    </b-modal>

    <!-- User Modal -->
    <b-modal
      id="modal-user"
      centered
      :title="modelType === 'editModel' ? 'Edit User' : 'Create User'"
      hide-footer
      no-close-on-backdrop
      size="lg"
      @hidden="hiddenUserModal"
    >
      <validation-observer ref="userForm">
        <b-form @submit.prevent="validUserForm">
          <!-- Name -->
          <b-form-group
            label="Name"
            label-for="name"
            class="required-label"
          >
            <validation-provider
              #default="{ errors }"
              name="Name"
              vid="name"
              rules="required|max:255"
            >
              <b-form-input
                id="name"
                v-model="name"
                type="text"
                :state="errors.length > 0 ? false : null"
                placeholder="Enter name"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Email -->
          <b-form-group
            label="Email"
            label-for="email"
            class="required-label"
          >
            <validation-provider
              #default="{ errors }"
              name="Email"
              vid="email"
              rules="required|email"
            >
              <b-form-input
                id="email"
                v-model="email"
                type="email"
                :state="errors.length > 0 ? false : null"
                placeholder="Enter email"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Multiple Roles -->
          <b-form-group
            label="Roles"
            label-for="role_ids"
          >
            <validation-provider
              #default="{ errors }"
              name="Roles"
              vid="role_ids"
            >
              <b-form-select
                id="role_ids"
                v-model="role_ids"
                :options="roleOptions"
                multiple
                :select-size="4"
                :state="errors.length > 0 ? false : null"
              >
                <template #first>
                  <option
                    value=""
                    disabled
                  >
                    Select roles (multiple selection)
                  </option>
                </template>
              </b-form-select>
              <small class="text-muted">Hold Ctrl/Cmd to select multiple roles</small>
              <small class="text-danger d-block">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Selected Roles Preview -->
          <b-form-group
            v-if="selectedRoles.length > 0"
            label="Selected Roles:"
            label-for="selected-roles-preview"
          >
            <div>
              <b-badge
                v-for="role in selectedRoles"
                :key="role.value"
                variant="info"
                class="mr-1 mb-1"
              >
                {{ role.text }}
                <feather-icon
                  icon="XIcon"
                  size="12"
                  class="cursor-pointer ml-1"
                  @click="removeRole(role.value)"
                />
              </b-badge>
            </div>
          </b-form-group>

          <!-- Password -->
          <b-form-group
            v-if="modelType !== 'editModel'"
            label="Password"
            label-for="password"
            class="required-label"
          >
            <validation-provider
              #default="{ errors }"
              name="Password"
              vid="password"
              rules="required|min:6"
            >
              <b-form-input
                id="password"
                v-model="password"
                type="password"
                :state="errors.length > 0 ? false : null"
                placeholder="Enter password"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Password Confirmation -->
          <b-form-group
            v-if="modelType !== 'editModel'"
            label="Confirm Password"
            label-for="password_confirmation"
            class="required-label"
          >
            <validation-provider
              #default="{ errors }"
              name="Password Confirmation"
              vid="password_confirmation"
              rules="required|confirmed:password"
            >
              <b-form-input
                id="password_confirmation"
                v-model="password_confirmation"
                type="password"
                :state="errors.length > 0 ? false : null"
                placeholder="Re-enter password"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Status -->
          <b-form-group
            label="Status"
            label-for="status"
            class="required-label"
          >
            <validation-provider
              #default="{ errors }"
              name="Status"
              vid="status"
              rules="required"
            >
              <b-form-select
                id="status"
                v-model="status"
                :options="statusOptions"
                :state="errors.length > 0 ? false : null"
              >
                <template #first>
                  <option
                    value=""
                    disabled
                  >
                    Select status
                  </option>
                </template>
              </b-form-select>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Submit -->
          <template v-if="isUserFormSubmitLoading">
            <b-button
              class="float-right"
              variant="primary"
              disabled
            >
              <b-spinner small /> Loading...
            </b-button>
          </template>
          <template v-else>
            <b-button
              type="submit"
              class="float-right"
              variant="primary"
            >
              Submit
            </b-button>
          </template>
        </b-form>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BForm, BFormGroup, BFormInput, BFormSelect,
  BModal, BSpinner, BPagination, VBToast, VBTooltip, BBadge,
  BListGroup, BListGroupItem,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import { required, max } from '@validations'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapGetters } from 'vuex'
import {
  USER_DELETE,
  USER_CREATE,
  USER_EDIT,
} from '@/helpers/superAdminPermissionConstant'

export default {
  name: 'UserView',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BModal,
    BSpinner,
    BPagination,
    VueGoodTable,
    ValidationProvider,
    ValidationObserver,
    BBadge,
    BListGroup,
    BListGroupItem,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      USER_CREATE,
      USER_EDIT,
      USER_DELETE,
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
      role_ids: [], // Changed to array for multiple selection
      status: 1,
      modelType: '',
      isUserFormSubmitLoading: false,
      userId: null,
      pageLength: 10,
      columns: [
        { label: 'SL', field: 'sl' },
        { label: 'Name', field: 'name' },
        { label: 'Email', field: 'email' },
        { label: 'Status', field: 'status_formated' },
        { label: 'Created At', field: 'created_at_formated' },
        { label: 'Action', field: 'action', sortable: false },
      ],
      rows: [],
      totalRecords: 0,
      isLoading: false,
      serverParams: {
        columnFilters: {},
        sort: [{ field: 'id', type: 'desc' }],
        page: 1,
        perPage: 10,
      },
      statusOptions: [
        { value: 1, text: 'Active' },
        { value: 2, text: 'Inactive' },
      ],
      roleOptions: [],
      selectedUser: {
        name: '',
        roles: [],
      },
    }
  },
  computed: {
    ...mapGetters({
      permissions: 'superAdminAuthModule/getPermissions',
    }),
    // Compute selected roles for preview
    selectedRoles() {
      if (!this.role_ids.length || !this.roleOptions.length) return []
      return this.roleOptions.filter(role => this.role_ids.includes(role.value))
    },
  },
  mounted() {
    this.loadItems()
    this.loadRoles()
  },
  methods: {
    async loadRoles() {
      try {
        const response = await this.$api.get('/api/super-admin/roles')
        this.roleOptions = response.data.data.map(role => ({
          value: role.id,
          text: role.name,
        }))
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'BellIcon',
            variant: 'danger',
            text: 'Failed to load roles',
          },
        })
      }
    },

    showUserModal() {
      this.$bvModal.show('modal-user')
    },

    hiddenUserModal() {
      this.$bvModal.hide('modal-user')
      this.resetUserModal()
    },

    async onShow(user) {
      this.modelType = 'editModel'
      this.name = user.name
      this.email = user.email
      this.status = user.status
      this.userId = user.id

      // Set multiple roles
      if (user.roles && user.roles.length > 0) {
        this.role_ids = user.roles.map(role => role.id)
      } else {
        this.role_ids = []
      }

      this.showUserModal()
    },

    // View roles in a separate modal
    viewRoles(user) {
      this.selectedUser = {
        name: user.name,
        roles: user.roles || [],
      }
      this.$bvModal.show('modal-view-roles')
    },

    // Remove role from selection
    removeRole(roleId) {
      this.role_ids = this.role_ids.filter(id => id !== roleId)
    },

    resetUserModal() {
      this.name = ''
      this.email = ''
      this.password = ''
      this.password_confirmation = ''
      this.role_ids = [] // Reset to empty array
      this.status = 1
      this.modelType = ''
      this.userId = null
    },

    updateParams(newProps) {
      this.serverParams = { ...this.serverParams, ...newProps }
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage })
      this.loadItems()
    },

    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage })
      this.loadItems()
    },

    onSortChange(params) {
      this.updateParams({ sort: params })
      this.loadItems()
    },

    onColumnFilter(params) {
      this.updateParams(params)
      this.loadItems()
    },

    async onDelete(user) {
      const { id, name } = user
      this.$swal({
        title: 'Warning!',
        text: `Are you sure you want to delete ${name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            const response = await this.$api.delete(`/api/super-admin/users/${id}`)
            this.loadItems()
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Success',
                icon: 'BellIcon',
                variant: 'success',
                text: response.data.message,
              },
            })
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                icon: 'BellIcon',
                variant: 'danger',
                text: error?.response?.data?.message,
              },
            })
          }
        }
      })
    },

    async validUserForm() {
      this.$refs.userForm.validate().then(async success => {
        if (success) {
          try {
            this.isUserFormSubmitLoading = true
            const formData = {
              name: this.name,
              email: this.email,
              role_ids: this.role_ids, // Send array of role IDs
              status: this.status,
            }

            if (this.modelType !== 'editModel') {
              formData.password = this.password
              formData.password_confirmation = this.password_confirmation
            }

            let response
            if (this.modelType === 'editModel') {
              response = await this.$api.put(`/api/super-admin/users/${this.userId}`, formData)
            } else {
              response = await this.$api.post('/api/super-admin/users', formData)
            }

            this.isUserFormSubmitLoading = false
            this.hiddenUserModal()
            this.loadItems()
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Success',
                icon: 'BellIcon',
                variant: 'success',
                text: response?.data?.message,
              },
            })
          } catch (error) {
            this.isUserFormSubmitLoading = false
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                icon: 'BellIcon',
                variant: 'danger',
                text: error?.response?.data?.message,
              },
            })
          }
        }
      })
    },

    async getUsers(params) {
      return await this.$api.get('/api/super-admin/users?include=roles', {
        params: {
          show: params.show,
          page: params.page,
          sort: params.sort,
        },
      })
    },

    async loadItems() {
      try {
        const res = await this.getUsers({
          show: this.serverParams.perPage,
          page: this.serverParams.page,
          sort: this.serverParams.sort,
        })
        this.rows = res?.data?.data
        this.totalRecords = res?.data?.meta?.pagination?.total
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Warning',
            icon: 'BellIcon',
            variant: 'warning',
            text: error?.response?.data?.message,
          },
        })
      }
    },
    viewRoles(user) {
      this.selectedUser = {
        name: user.name,
        roles: user.roles?.data || [],
      }
      this.$bvModal.show('modal-view-roles')
    },
    hiddenRolesModal() {
      this.$bvModal.hide('modal-view-roles')
    },

  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";

// Custom styles for multi-select and badges
.multiselect__tag {
    background: #7367f0;
}

.multiselect__tag-icon:after {
    color: white;
}

.multiselect__tag-icon:hover {
    background: #5e50ee;
}

.badge {
    cursor: pointer;
}

.custom-icon {
    transition: color 0.2s ease-in-out;

    &:hover {
        color: #7367f0;
    }
}

.cursor-pointer {
    cursor: pointer;
}
</style>
